import type { Locale } from "@/i18n";

export type LegalSection = { heading: string; body: string };
export type LegalContent = Record<Locale, LegalSection[]>;

const OPERATOR = {
  nameRo: "CHETREANU MATEI-RADU PERSOANĂ FIZICĂ AUTORIZATĂ",
  nameEn: "Matei-Radu Chetreanu, Authorized Sole Proprietor (PFA)",
  nameDe: "Matei-Radu Chetreanu, eingetragener Einzelunternehmer (PFA)",
  cui: "54624380",
  rc: "F2026023727001",
  address: "B-dul Bucureștii Noi 136, et. Parter, ap. 5, Sector 1, București, România",
  hello: '<a href="mailto:hello@vorba.dev">hello@vorba.dev</a>',
  privacy: '<a href="mailto:privacy@vorba.dev">privacy@vorba.dev</a>',
  updated: "18 mai 2026",
};

/* ============================================================
 * PRIVACY POLICY
 * ============================================================ */
export const PRIVACY: LegalContent = {
  ro: [
    {
      heading: "1. Introducere",
      body: `Această politică explică modul în care ${OPERATOR.nameRo} (denumit în continuare „vorba", „noi" sau „Operatorul") colectează, utilizează și protejează datele dvs. cu caracter personal atunci când vizitați site-ul vorba.dev sau utilizați serviciile noastre.

Ne angajăm să respectăm Regulamentul (UE) 2016/679 (GDPR) și Legea nr. 190/2018 privind măsuri de punere în aplicare a GDPR în România.`,
    },
    {
      heading: "2. Identitatea Operatorului",
      body: `${OPERATOR.nameRo}<br>
CUI: ${OPERATOR.cui}<br>
Nr. ONRC: ${OPERATOR.rc}<br>
Adresa profesională: ${OPERATOR.address}<br>
Brand comercial: vorba (vorba.dev)<br>
Email contact pentru chestiuni de confidențialitate: ${OPERATOR.privacy}`,
    },
    {
      heading: "3. Categorii de date colectate",
      body: `<strong>(a) Date furnizate direct prin formularul de contact:</strong>
<ul>
<li>Nume și prenume</li>
<li>Adresa de email</li>
<li>Numele companiei (opțional)</li>
<li>Conținutul mesajului (descrierea proiectului/problemei)</li>
</ul>
<strong>(b) Date colectate automat la vizitarea site-ului:</strong>
<ul>
<li>Adresa IP (logată tehnic de furnizorul de hosting)</li>
<li>Data și ora vizitei</li>
<li>Pagini vizitate</li>
<li>Tipul browserului și sistemului de operare</li>
</ul>
<strong>(c) Date colectate pentru clienții activi (în cadrul contractului):</strong>
<ul>
<li>Date de identificare companie (CUI, denumire, adresă)</li>
<li>Date de facturare conform legislației fiscale române</li>
<li>Date de contact ale persoanelor desemnate pentru proiect</li>
</ul>`,
    },
    {
      heading: "4. Scopuri și temei legal al prelucrării",
      body: `<table>
<thead><tr><th>Scop</th><th>Temei legal (Art. GDPR)</th></tr></thead>
<tbody>
<tr><td>Răspuns la solicitări via formularul de contact</td><td>Interes legitim — Art. 6(1)(f) (pre-contractual)</td></tr>
<tr><td>Executarea contractelor cu clienții</td><td>Contract — Art. 6(1)(b)</td></tr>
<tr><td>Emiterea facturilor și obligații fiscale</td><td>Obligație legală — Art. 6(1)(c)</td></tr>
<tr><td>Comunicări marketing direct (cu abonare)</td><td>Consimțământ — Art. 6(1)(a)</td></tr>
<tr><td>Securitate site și prevenire abuz</td><td>Interes legitim — Art. 6(1)(f)</td></tr>
</tbody></table>`,
    },
    {
      heading: "5. Destinatari (procesatori)",
      body: `Folosim următorii procesatori care pot avea acces la datele dvs., toți cu acorduri DPA semnate conform Art. 28 GDPR:
<ul>
<li>Supabase Inc. — infrastructură baze de date — UE region (Frankfurt)</li>
<li>Google LLC (Google Workspace) — email business — UE/SUA, DPF certified</li>
<li>Stripe Inc. — procesare plăți — SUA, DPF certified</li>
<li>Cloudflare Inc. — CDN, hosting și securitate — UE/Global</li>
<li>Resend (Plus Five Five, Inc.) — livrare email tranzacțional — SUA, DPF certified</li>
<li>ElevenLabs Inc. — transcriere vocală (funcția „speak it") — SUA</li>
<li>Anthropic PBC — procesare AI (instrumentul de analiză a stack-ului) — SUA</li>
</ul>
Nu vindem și nu transferăm datele dvs. către terți pentru scopuri de marketing.`,
    },
    {
      heading: "6. Transferuri internaționale",
      body: `Anumiți procesatori (Google, Stripe) pot procesa date în SUA. Aceste transferuri sunt protejate prin:
<ul>
<li>Clauze Contractuale Standard (SCC) emise de Comisia Europeană</li>
<li>Aderarea la EU-U.S. Data Privacy Framework (DPF)</li>
</ul>`,
    },
    {
      heading: "7. Perioada de stocare",
      body: `<ul>
<li>Lead-uri necontactate: maximum 24 luni</li>
<li>Lead-uri în proces de calificare: 36 luni</li>
<li>Date clienți activi: durata contractului + 10 ani (obligație fiscală RO)</li>
<li>Comunicări email: maximum 24 luni de la ultima interacțiune</li>
<li>Loguri tehnice: maximum 12 luni</li>
</ul>`,
    },
    {
      heading: "8. Drepturile dvs.",
      body: `Conform art. 15-22 GDPR aveți dreptul la:
<ul>
<li>Acces — confirmarea că datele dvs. sunt prelucrate și o copie</li>
<li>Rectificare — corectarea datelor inexacte</li>
<li>Ștergere — eliminarea datelor (în condițiile GDPR)</li>
<li>Restricționare — limitarea prelucrării în anumite cazuri</li>
<li>Portabilitate — primirea datelor într-un format structurat</li>
<li>Opoziție — la prelucrarea pe baza interesului legitim</li>
<li>Retragere consimțământ — pentru prelucrările bazate pe consimțământ</li>
</ul>
Pentru exercitarea acestor drepturi: ${OPERATOR.privacy}. Vom răspunde în maximum 30 de zile.`,
    },
    {
      heading: "9. Dreptul de a depune plângere",
      body: `Dacă considerați că drepturile dvs. au fost încălcate, puteți depune plângere la:

<strong>ANSPDCP (România)</strong> — Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal<br>
B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, București<br>
Web: <a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a><br>
Email: <a href="mailto:anspdcp@dataprotection.ro">anspdcp@dataprotection.ro</a>

Pentru vizitatori din Elveția: <strong>EDÖB</strong> — <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener">www.edoeb.admin.ch</a>`,
    },
    {
      heading: "10. Securitatea datelor",
      body: `Implementăm măsuri tehnice și organizatorice rezonabile:
<ul>
<li>Criptare TLS pentru toate comunicările</li>
<li>Acces restricționat la baza de date (Bearer tokens)</li>
<li>Row-Level Security (RLS) pe toate tabelele</li>
<li>Audit logs și monitorizare acces</li>
<li>Procesatori certificați (ISO 27001, SOC 2)</li>
</ul>`,
    },
    {
      heading: "11. Cookies",
      body: `Site-ul folosește exclusiv cookie-uri strict necesare:
<ul>
<li><code>vorba_lang</code> — preferința dvs. de limbă (durată 1 an, fără date personale)</li>
</ul>
Pentru zona /crm (acces restricționat), folosim localStorage pentru sesiunea de autentificare.

Nu folosim cookie-uri de marketing, analytics sau publicitate.`,
    },
    {
      heading: "12. Prelucrare automatizată",
      body: `Nu efectuăm prelucrare automatizată cu efecte juridice asupra dvs. (ex: profilare). Datele dvs. sunt evaluate de o persoană umană.`,
    },
    {
      heading: "13. Modificări ale politicii",
      body: `Această politică poate fi actualizată periodic. Versiunea curentă este accesibilă mereu la vorba.dev/privacy. Modificările materiale vor fi anunțate prin email clienților activi.`,
    },
    {
      heading: "14. Contact",
      body: `Pentru orice întrebări despre prelucrarea datelor:
<ul>
<li>Email: ${OPERATOR.privacy}</li>
<li>Adresă: ${OPERATOR.address}</li>
</ul>
Ultima actualizare: ${OPERATOR.updated}`,
    },
  ],
  en: [
    {
      heading: "1. Introduction",
      body: `This Privacy Policy explains how ${OPERATOR.nameEn} (referred to as "vorba", "we", or "the Controller") collects, uses, and protects your personal data when you visit vorba.dev or use our services.

We are committed to complying with Regulation (EU) 2016/679 (GDPR) and Romanian Law 190/2018 implementing GDPR.`,
    },
    {
      heading: "2. Identity of the Controller",
      body: `${OPERATOR.nameEn}<br>
Tax ID (CIF): ${OPERATOR.cui}<br>
Romanian Trade Registry: ${OPERATOR.rc}<br>
Business address: ${OPERATOR.address}<br>
Commercial brand: vorba (vorba.dev)<br>
Privacy contact: ${OPERATOR.privacy}`,
    },
    {
      heading: "3. Categories of data collected",
      body: `<strong>(a) Data you provide via contact form:</strong>
<ul>
<li>First and last name</li>
<li>Email address</li>
<li>Company name (optional)</li>
<li>Message content (project/problem description)</li>
</ul>
<strong>(b) Data collected automatically during your visit:</strong>
<ul>
<li>IP address (logged technically by hosting provider)</li>
<li>Visit date and time</li>
<li>Pages visited</li>
<li>Browser and operating system type</li>
</ul>
<strong>(c) Data collected for active clients (under contract):</strong>
<ul>
<li>Company identification (Tax ID, name, address)</li>
<li>Billing data per Romanian fiscal legislation</li>
<li>Contact data of designated project personnel</li>
</ul>`,
    },
    {
      heading: "4. Purposes and legal basis",
      body: `<table>
<thead><tr><th>Purpose</th><th>Legal basis (GDPR Art.)</th></tr></thead>
<tbody>
<tr><td>Responding to contact form inquiries</td><td>Legitimate interest — Art. 6(1)(f) (pre-contractual)</td></tr>
<tr><td>Performing client contracts</td><td>Contract — Art. 6(1)(b)</td></tr>
<tr><td>Issuing invoices and fiscal obligations</td><td>Legal obligation — Art. 6(1)(c)</td></tr>
<tr><td>Direct marketing communications (opt-in)</td><td>Consent — Art. 6(1)(a)</td></tr>
<tr><td>Site security and abuse prevention</td><td>Legitimate interest — Art. 6(1)(f)</td></tr>
</tbody></table>`,
    },
    {
      heading: "5. Recipients (processors)",
      body: `We use the following processors, all with signed DPAs per Art. 28 GDPR:
<ul>
<li>Supabase Inc. — database infrastructure — EU region (Frankfurt)</li>
<li>Google LLC (Workspace) — business email — EU/US, DPF certified</li>
<li>Stripe Inc. — payment processing — US, DPF certified</li>
<li>Cloudflare Inc. — CDN, hosting and security — EU/Global</li>
<li>Resend (Plus Five Five, Inc.) — transactional email delivery — US, DPF certified</li>
<li>ElevenLabs Inc. — voice transcription ("speak it" feature) — US</li>
<li>Anthropic PBC — AI processing (stack analysis tool) — US</li>
</ul>
We do not sell or transfer your data to third parties for marketing purposes.`,
    },
    {
      heading: "6. International transfers",
      body: `Some processors (Google, Stripe) may process data in the US. These transfers are protected by:
<ul>
<li>Standard Contractual Clauses (SCC) issued by the European Commission</li>
<li>Adherence to the EU-U.S. Data Privacy Framework (DPF)</li>
</ul>`,
    },
    {
      heading: "7. Retention period",
      body: `<ul>
<li>Unconverted leads: max 24 months</li>
<li>Leads in qualification: max 36 months</li>
<li>Active client data: contract duration + 10 years (RO fiscal obligation)</li>
<li>Email communications: max 24 months after last interaction</li>
<li>Technical logs: max 12 months</li>
</ul>`,
    },
    {
      heading: "8. Your rights",
      body: `Under GDPR Articles 15-22 you have the right to:
<ul>
<li>Access — confirmation that your data is processed plus a copy</li>
<li>Rectification — correction of inaccurate data</li>
<li>Erasure — deletion (under GDPR conditions)</li>
<li>Restriction — limiting processing in certain cases</li>
<li>Portability — receiving data in structured format</li>
<li>Objection — against processing based on legitimate interest</li>
<li>Withdraw consent — for consent-based processing</li>
</ul>
To exercise these: ${OPERATOR.privacy}. We respond within 30 days.`,
    },
    {
      heading: "9. Right to lodge a complaint",
      body: `If you believe your rights have been violated, you may file a complaint with:

<strong>ANSPDCP (Romania)</strong> — National Supervisory Authority for Personal Data Processing<br>
B-dul G-ral. Gheorghe Magheru 28-30, Sector 1, Bucharest<br>
Web: <a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a>

For visitors from Switzerland: <strong>FDPIC / EDÖB</strong> — <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener">www.edoeb.admin.ch</a>`,
    },
    {
      heading: "10. Data security",
      body: `We implement reasonable technical and organizational measures:
<ul>
<li>TLS encryption for all communications</li>
<li>Restricted database access (Bearer tokens)</li>
<li>Row-Level Security (RLS) on all tables</li>
<li>Audit logs and access monitoring</li>
<li>Certified processors (ISO 27001, SOC 2)</li>
</ul>`,
    },
    {
      heading: "11. Cookies",
      body: `Our site uses strictly necessary cookies only:
<ul>
<li><code>vorba_lang</code> — your language preference (1-year duration, no personal data)</li>
</ul>
For the /crm area (restricted access), we use localStorage for authentication session.

We do not use marketing, analytics, or advertising cookies.`,
    },
    {
      heading: "12. Automated decision-making",
      body: `We do not engage in automated decision-making with legal effects on you (e.g., profiling). Your data is evaluated by a human.`,
    },
    {
      heading: "13. Changes to this policy",
      body: `This policy may be updated periodically. The current version is always available at vorba.dev/privacy. Material changes are announced via email to active clients.`,
    },
    {
      heading: "14. Contact",
      body: `For any questions about data processing:
<ul>
<li>Email: ${OPERATOR.privacy}</li>
<li>Postal: ${OPERATOR.address}</li>
</ul>
Last updated: ${OPERATOR.updated}`,
    },
  ],
  de: [
    {
      heading: "1. Einführung",
      body: `Diese Datenschutzerklärung erläutert, wie ${OPERATOR.nameDe} (nachfolgend „vorba", „wir" oder „Verantwortlicher") Ihre personenbezogenen Daten erhebt, verwendet und schützt, wenn Sie vorba.dev besuchen oder unsere Dienstleistungen in Anspruch nehmen.

Wir verpflichten uns zur Einhaltung der Verordnung (EU) 2016/679 (DSGVO) sowie des rumänischen Gesetzes Nr. 190/2018 zur Umsetzung der DSGVO.`,
    },
    {
      heading: "2. Identität des Verantwortlichen",
      body: `${OPERATOR.nameDe}<br>
Steuernummer (CIF): ${OPERATOR.cui}<br>
Rumänisches Handelsregister: ${OPERATOR.rc}<br>
Geschäftsadresse: ${OPERATOR.address}<br>
Marke: vorba (vorba.dev)<br>
Datenschutzkontakt: ${OPERATOR.privacy}`,
    },
    {
      heading: "3. Kategorien erhobener Daten",
      body: `<strong>(a) Direkt über das Kontaktformular bereitgestellte Daten:</strong>
<ul>
<li>Vor- und Nachname</li>
<li>E-Mail-Adresse</li>
<li>Firmenname (optional)</li>
<li>Inhalt der Nachricht (Projekt-/Problembeschreibung)</li>
</ul>
<strong>(b) Beim Besuch automatisch erhobene Daten:</strong>
<ul>
<li>IP-Adresse (technisch durch den Hosting-Anbieter protokolliert)</li>
<li>Datum und Uhrzeit des Besuchs</li>
<li>Aufgerufene Seiten</li>
<li>Browser- und Betriebssystemtyp</li>
</ul>
<strong>(c) Für aktive Kunden (im Rahmen des Vertrags) erhobene Daten:</strong>
<ul>
<li>Unternehmensidentifikation (Steuernummer, Name, Adresse)</li>
<li>Rechnungsdaten gemäß rumänischem Steuerrecht</li>
<li>Kontaktdaten der für das Projekt benannten Personen</li>
</ul>`,
    },
    {
      heading: "4. Zwecke und Rechtsgrundlagen der Verarbeitung",
      body: `<table>
<thead><tr><th>Zweck</th><th>Rechtsgrundlage (Art. DSGVO)</th></tr></thead>
<tbody>
<tr><td>Beantwortung von Anfragen über das Kontaktformular</td><td>Berechtigtes Interesse — Art. 6 Abs. 1 lit. f (vorvertraglich)</td></tr>
<tr><td>Erfüllung von Kundenverträgen</td><td>Vertragserfüllung — Art. 6 Abs. 1 lit. b</td></tr>
<tr><td>Rechnungsstellung und steuerliche Pflichten</td><td>Rechtliche Verpflichtung — Art. 6 Abs. 1 lit. c</td></tr>
<tr><td>Direktwerbung (mit Einwilligung)</td><td>Einwilligung — Art. 6 Abs. 1 lit. a</td></tr>
<tr><td>Websicherheit und Missbrauchsprävention</td><td>Berechtigtes Interesse — Art. 6 Abs. 1 lit. f</td></tr>
</tbody></table>`,
    },
    {
      heading: "5. Empfänger (Auftragsverarbeiter)",
      body: `Wir nutzen die folgenden Auftragsverarbeiter, alle mit unterzeichneten Auftragsverarbeitungsverträgen (AVV) gemäß Art. 28 DSGVO:
<ul>
<li>Supabase Inc. — Datenbankinfrastruktur — EU-Region (Frankfurt)</li>
<li>Google LLC (Workspace) — Geschäfts-E-Mail — EU/USA, DPF-zertifiziert</li>
<li>Stripe Inc. — Zahlungsabwicklung — USA, DPF-zertifiziert</li>
<li>Cloudflare Inc. — CDN, Hosting und Sicherheit — EU/Global</li>
<li>Resend (Plus Five Five, Inc.) — Versand transaktionaler E-Mails — USA, DPF-zertifiziert</li>
<li>ElevenLabs Inc. — Sprachtranskription („speak it"-Funktion) — USA</li>
<li>Anthropic PBC — KI-Verarbeitung (Stack-Analyse-Tool) — USA</li>
</ul>
Wir verkaufen oder übertragen Ihre Daten nicht zu Marketingzwecken an Dritte.`,
    },
    {
      heading: "6. Internationale Datenübermittlungen",
      body: `Einige Auftragsverarbeiter (Google, Stripe) können Daten in den USA verarbeiten. Diese Übermittlungen sind geschützt durch:
<ul>
<li>Standardvertragsklauseln (SCC) der Europäischen Kommission</li>
<li>Beitritt zum EU-U.S. Data Privacy Framework (DPF)</li>
</ul>`,
    },
    {
      heading: "7. Speicherdauer",
      body: `<ul>
<li>Nicht konvertierte Leads: max. 24 Monate</li>
<li>Leads in Qualifizierung: max. 36 Monate</li>
<li>Daten aktiver Kunden: Vertragsdauer + 10 Jahre (rumänische steuerliche Pflicht)</li>
<li>E-Mail-Kommunikation: max. 24 Monate ab letzter Interaktion</li>
<li>Technische Logs: max. 12 Monate</li>
</ul>`,
    },
    {
      heading: "8. Ihre Rechte",
      body: `Gemäß Art. 15-22 DSGVO haben Sie das Recht auf:
<ul>
<li>Auskunft — Bestätigung der Verarbeitung und eine Kopie</li>
<li>Berichtigung — Korrektur unrichtiger Daten</li>
<li>Löschung — Entfernung der Daten (unter den Bedingungen der DSGVO)</li>
<li>Einschränkung — Begrenzung der Verarbeitung in bestimmten Fällen</li>
<li>Datenübertragbarkeit — Erhalt der Daten in strukturiertem Format</li>
<li>Widerspruch — gegen Verarbeitung auf Basis berechtigten Interesses</li>
<li>Widerruf der Einwilligung — für einwilligungsbasierte Verarbeitungen</li>
</ul>
Zur Ausübung dieser Rechte: ${OPERATOR.privacy}. Wir antworten innerhalb von 30 Tagen.`,
    },
    {
      heading: "9. Beschwerderecht",
      body: `Wenn Sie der Ansicht sind, dass Ihre Rechte verletzt wurden, können Sie Beschwerde einreichen bei:

<strong>ANSPDCP (Rumänien)</strong> — Rumänische Aufsichtsbehörde für die Verarbeitung personenbezogener Daten<br>
B-dul G-ral. Gheorghe Magheru 28-30, Sektor 1, Bukarest<br>
Web: <a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a>

Für Besucher aus der Schweiz: <strong>EDÖB</strong> — Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragter — <a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener">www.edoeb.admin.ch</a>`,
    },
    {
      heading: "10. Datensicherheit",
      body: `Wir setzen angemessene technische und organisatorische Maßnahmen um:
<ul>
<li>TLS-Verschlüsselung für jede Kommunikation</li>
<li>Beschränkter Datenbankzugriff (Bearer-Tokens)</li>
<li>Row-Level-Security (RLS) auf allen Tabellen</li>
<li>Audit-Logs und Zugriffsüberwachung</li>
<li>Zertifizierte Auftragsverarbeiter (ISO 27001, SOC 2)</li>
</ul>`,
    },
    {
      heading: "11. Cookies",
      body: `Unsere Website verwendet ausschließlich technisch notwendige Cookies:
<ul>
<li><code>vorba_lang</code> — Ihre Sprachpräferenz (Dauer 1 Jahr, keine personenbezogenen Daten)</li>
</ul>
Für den Bereich /crm (beschränkter Zugang) verwenden wir localStorage für die Sitzung.

Wir verwenden keine Marketing-, Analyse- oder Werbe-Cookies.`,
    },
    {
      heading: "12. Automatisierte Entscheidungsfindung",
      body: `Wir führen keine automatisierte Entscheidungsfindung mit rechtlicher Wirkung für Sie durch (z. B. Profiling). Ihre Daten werden von einem Menschen bewertet.`,
    },
    {
      heading: "13. Änderungen dieser Richtlinie",
      body: `Diese Richtlinie kann periodisch aktualisiert werden. Die aktuelle Version ist jederzeit unter vorba.dev/privacy verfügbar. Wesentliche Änderungen werden aktiven Kunden per E-Mail mitgeteilt.`,
    },
    {
      heading: "14. Kontakt",
      body: `Für Fragen zur Datenverarbeitung:
<ul>
<li>E-Mail: ${OPERATOR.privacy}</li>
<li>Post: ${OPERATOR.address}</li>
</ul>
Letzte Aktualisierung: ${OPERATOR.updated}`,
    },
  ],
};

/* ============================================================
 * TERMS & CONDITIONS
 * ============================================================ */
export const TERMS: LegalContent = {
  ro: [
    {
      heading: "1. Introducere și acceptare",
      body: `Acești Termeni și Condiții („Termenii") reglementează utilizarea site-ului vorba.dev și a serviciilor profesionale oferite de ${OPERATOR.nameRo} (denumit „vorba", „noi" sau „Furnizorul").

Prin utilizarea site-ului sau prin contractarea serviciilor noastre, sunteți de acord cu acești Termeni. Dacă nu sunteți de acord, vă rugăm să nu utilizați serviciile.`,
    },
    {
      heading: "2. Identificarea Furnizorului",
      body: `${OPERATOR.nameRo}<br>
CUI: ${OPERATOR.cui}<br>
Nr. ONRC: ${OPERATOR.rc}<br>
Adresa profesională: ${OPERATOR.address}<br>
Brand: vorba (vorba.dev)<br>
Email: ${OPERATOR.hello}`,
    },
    {
      heading: "3. Servicii oferite",
      body: `vorba oferă servicii profesionale de:
<ul>
<li>Dezvoltare software custom (aplicații web, sisteme interne, agenți AI)</li>
<li>Audit tehnic și de digitalizare</li>
<li>Consultanță strategică în arhitectură software</li>
<li>Implementare și integrare sisteme</li>
</ul>
Serviciile sunt destinate EXCLUSIV clienților business (B2B). Nu oferim servicii consumatorilor (B2C).`,
    },
    {
      heading: "4. Procesul de contractare",
      body: `<ol>
<li>Solicitare inițială — prin formularul de pe site sau direct la ${OPERATOR.hello}</li>
<li>Audit gratuit de 48 ore — analiză preliminară și plan inițial, fără obligație</li>
<li>Propunere comercială — detaliată, cu scope, livrabile, preț fix</li>
<li>Contract de prestări servicii separat — semnat înaintea începerii oricărei lucrări</li>
<li>Execuție și livrare — conform milestone-urilor agreate</li>
<li>Acceptare și plată finală</li>
</ol>
Audit-ul gratuit de 48 ore este o ofertă comercială fără obligație contractuală pentru nicio parte.`,
    },
    {
      heading: "5. Prețuri și plată",
      body: `<ul>
<li>Prețurile sunt afișate exclusiv pentru clienții B2B</li>
<li>Modelul standard: preț fix per proiect (fixed-price), nu pe oră</li>
<li>Plata conform schedule-ului din contractul individual (milestone-uri)</li>
<li>Modalități: transfer bancar, card via Stripe</li>
<li>Toate prețurile în EUR sau RON, fără TVA în cazul PFA cu plafon (TVA se adaugă dacă devenim plătitori)</li>
<li>Facturile sunt emise conform legislației fiscale române și transmise prin SPV/email</li>
<li>Termen de plată: maxim 14 zile de la emiterea facturii (dacă nu altfel specificat)</li>
</ul>`,
    },
    {
      heading: "6. Livrare și acceptare",
      body: `<ul>
<li>Termenul de livrare este stipulat în contractul individual</li>
<li>Considerăm un livrabil acceptat dacă nu primim feedback scris în 7 zile lucrătoare de la livrare</li>
<li>Modificările de scope după aprobare se taxează separat (change request)</li>
</ul>`,
    },
    {
      heading: "7. Proprietate intelectuală",
      body: `<ul>
<li>Codul sursă custom dezvoltat pentru client devine proprietatea exclusivă a clientului la momentul plății finale</li>
<li>Componentele generice, biblioteci open-source și framework-uri rămân la sursa originală</li>
<li>Tehnicile, metodologiile și know-how-ul vorba rămân proprietatea vorba</li>
<li>vorba poate face referință publică la proiect (case study) doar cu acordul expres al clientului</li>
</ul>`,
    },
    {
      heading: "8. Confidențialitate",
      body: `vorba și clientul vor păstra confidențialitatea informațiilor de business primite în cadrul colaborării, pe o durată de 5 ani după încetarea contractului.

Excepții:
<ul>
<li>Informații deja publice</li>
<li>Informații solicitate de autoritățile competente</li>
<li>Informații tehnice generice (best practices, abordări arhitecturale)</li>
</ul>`,
    },
    {
      heading: "9. Prelucrarea datelor cu caracter personal",
      body: `În cadrul executării contractului, vorba poate avea acces la date personale ale clientului. În aceste cazuri, vorba acționează ca PROCESATOR (data processor), iar clientul este OPERATOR (data controller). Va fi semnat un Acord de Procesare a Datelor (DPA) separat, conform Art. 28 GDPR.

Pentru detalii despre prelucrarea datelor vizitatorilor site-ului, consultați <a href="/privacy">Politica de Confidențialitate</a>.`,
    },
    {
      heading: "10. Garanții și limitări",
      body: `<ul>
<li>vorba garantează că serviciile sunt prestate cu profesionalismul rezonabil așteptat în industria software</li>
<li>Pentru bug-urile semnalate în 30 zile de la livrare, vorba se obligă la remedierea gratuită</li>
<li>După 30 zile, suport tehnic este disponibil pe bază de contract de mentenanță separat</li>
<li>Răspunderea totală a vorba este limitată la valoarea plătită în baza contractului individual</li>
</ul>
NU răspundem pentru:
<ul>
<li>Pierderi de profit sau oportunitate</li>
<li>Daune indirecte sau consecvențiale</li>
<li>Costuri de oportunitate</li>
<li>Pierderi cauzate de furnizori terți (cloud, SaaS)</li>
</ul>`,
    },
    {
      heading: "11. Forță majoră",
      body: `Niciuna dintre părți nu este răspunzătoare pentru întârzieri sau neexecutare cauzate de evenimente de forță majoră (război, dezastre naturale, pandemii oficial declarate, întreruperi majore de infrastructură internet).`,
    },
    {
      heading: "12. Încetarea contractului",
      body: `Orice parte poate denunța contractul:
<ul>
<li>De comun acord</li>
<li>Pentru încălcare materială, după notificare scrisă și termen de remediere de 14 zile</li>
<li>Pentru insolvabilitate sau procedură de faliment a celeilalte părți</li>
</ul>
La încetare:
<ul>
<li>Clientul plătește pentru lucrul efectuat până la data încetării</li>
<li>vorba returnează toate datele clientului și șterge copiile interne în 30 zile</li>
</ul>`,
    },
    {
      heading: "13. Legislație aplicabilă și jurisdicție",
      body: `Contractul este guvernat de legea română. Orice litigiu va fi soluționat:
<ul>
<li>Amiabil prin negociere directă</li>
<li>Prin mediere SAL.ro (opțional)</li>
<li>În ultimă instanță, de către tribunalele competente de la sediul vorba (București)</li>
</ul>
Pentru clienți din Elveția: aplicăm legea română, dar acceptăm și jurisdicția alternativă a tribunalelor din Zürich pentru contracte sub 50.000 CHF.`,
    },
    {
      heading: "14. Notificări",
      body: `Toate notificările oficiale se transmit prin email la:
<ul>
<li>vorba: ${OPERATOR.hello} (cu confirmare de primire)</li>
<li>Client: la adresa de contact furnizată la contractare</li>
</ul>`,
    },
    {
      heading: "15. Modificări",
      body: `Acești Termeni pot fi modificați. Modificările vor fi anunțate clienților activi cu minim 30 zile înainte de intrarea în vigoare. Continuarea utilizării serviciilor după modificare echivalează cu acceptarea noilor Termeni.`,
    },
    {
      heading: "16. Nulitate parțială și întreaga înțelegere",
      body: `Dacă o clauză din acești Termeni este declarată nulă, restul rămâne în vigoare.

Acești Termeni, împreună cu contractul individual de prestări servicii și Politica de Confidențialitate, reprezintă întreaga înțelegere între părți.

Ultima actualizare: ${OPERATOR.updated}`,
    },
  ],
  en: [
    {
      heading: "1. Introduction and acceptance",
      body: `These Terms and Conditions (the "Terms") govern your use of vorba.dev and the professional services offered by ${OPERATOR.nameEn} (referred to as "vorba", "we", or "the Provider").

By using our site or contracting our services, you agree to these Terms. If you do not agree, please do not use the services.`,
    },
    {
      heading: "2. Provider identification",
      body: `${OPERATOR.nameEn}<br>
Tax ID (CIF): ${OPERATOR.cui}<br>
Trade Registry: ${OPERATOR.rc}<br>
Business address: ${OPERATOR.address}<br>
Brand: vorba (vorba.dev)<br>
Email: ${OPERATOR.hello}`,
    },
    {
      heading: "3. Services offered",
      body: `vorba offers professional services in:
<ul>
<li>Custom software development (web apps, internal systems, AI agents)</li>
<li>Technical audit and digital transformation</li>
<li>Strategic consulting on software architecture</li>
<li>System implementation and integration</li>
</ul>
Services are intended EXCLUSIVELY for business (B2B) clients. We do not offer consumer (B2C) services.`,
    },
    {
      heading: "4. Contracting process",
      body: `<ol>
<li>Initial inquiry — via the website form or directly to ${OPERATOR.hello}</li>
<li>Free 48-hour audit — preliminary analysis and initial plan, no obligation</li>
<li>Commercial proposal — detailed, with scope, deliverables, fixed price</li>
<li>Separate service contract — signed before any work begins</li>
<li>Execution and delivery — per agreed milestones</li>
<li>Acceptance and final payment</li>
</ol>
The free 48-hour audit is a commercial offer without contractual obligation for either party.`,
    },
    {
      heading: "5. Pricing and payment",
      body: `<ul>
<li>Prices displayed are for B2B clients only</li>
<li>Standard model: fixed price per project, not hourly</li>
<li>Payment per the individual contract's milestone schedule</li>
<li>Methods: bank transfer, card via Stripe</li>
<li>All prices in EUR or RON, VAT-exclusive while PFA is below VAT threshold</li>
<li>Invoices are issued per Romanian fiscal law and transmitted via SPV/email</li>
<li>Payment terms: maximum 14 days from invoice issuance unless otherwise specified</li>
</ul>`,
    },
    {
      heading: "6. Delivery and acceptance",
      body: `<ul>
<li>Delivery deadline is stipulated in the individual contract</li>
<li>A deliverable is considered accepted if no written feedback is received within 7 business days of delivery</li>
<li>Scope changes after approval are billed separately (change request)</li>
</ul>`,
    },
    {
      heading: "7. Intellectual property",
      body: `<ul>
<li>Custom source code developed for the client becomes the exclusive property of the client upon final payment</li>
<li>Generic components, open-source libraries, and frameworks remain at their original source</li>
<li>vorba's techniques, methodologies, and know-how remain vorba's property</li>
<li>vorba may publicly reference the project (case study) only with the client's express consent</li>
</ul>`,
    },
    {
      heading: "8. Confidentiality",
      body: `vorba and the client will maintain confidentiality of business information received during the collaboration for 5 years after contract termination.

Exceptions:
<ul>
<li>Information already public</li>
<li>Information requested by competent authorities</li>
<li>Generic technical information (best practices, architectural approaches)</li>
</ul>`,
    },
    {
      heading: "9. Personal data processing",
      body: `During contract execution, vorba may access the client's personal data. In such cases, vorba acts as the PROCESSOR and the client as CONTROLLER. A separate Data Processing Agreement (DPA) will be signed per Art. 28 GDPR.

For details on site visitor data processing, see our <a href="/privacy">Privacy Policy</a>.`,
    },
    {
      heading: "10. Warranties and limitations",
      body: `<ul>
<li>vorba warrants that services are performed with the reasonable professionalism expected in the software industry</li>
<li>For bugs reported within 30 days of delivery, vorba commits to free remediation</li>
<li>After 30 days, technical support is available under a separate maintenance contract</li>
<li>vorba's total liability is capped at the amount paid under the individual contract</li>
</ul>
We are NOT liable for:
<ul>
<li>Lost profits or opportunity</li>
<li>Indirect or consequential damages</li>
<li>Opportunity costs</li>
<li>Losses caused by third-party providers (cloud, SaaS)</li>
</ul>`,
    },
    {
      heading: "11. Force majeure",
      body: `Neither party is liable for delays or non-performance caused by force majeure events (war, natural disasters, officially declared pandemics, major internet infrastructure outages).`,
    },
    {
      heading: "12. Termination",
      body: `Either party may terminate the contract:
<ul>
<li>By mutual agreement</li>
<li>For material breach, after written notice and 14-day cure period</li>
<li>For insolvency or bankruptcy proceedings of the other party</li>
</ul>
Upon termination:
<ul>
<li>The client pays for work performed up to the termination date</li>
<li>vorba returns all client data and deletes internal copies within 30 days</li>
</ul>`,
    },
    {
      heading: "13. Governing law and jurisdiction",
      body: `The contract is governed by Romanian law. Any dispute will be resolved:
<ul>
<li>Amicably through direct negotiation</li>
<li>Through SAL.ro mediation (optional)</li>
<li>Ultimately by the competent courts at vorba's seat (Bucharest)</li>
</ul>
For Swiss clients: we apply Romanian law but accept the alternative jurisdiction of Zürich courts for contracts under CHF 50,000.`,
    },
    {
      heading: "14. Notices",
      body: `All official notices are transmitted by email to:
<ul>
<li>vorba: ${OPERATOR.hello} (with delivery confirmation)</li>
<li>Client: at the contact address provided at contracting</li>
</ul>`,
    },
    {
      heading: "15. Modifications",
      body: `These Terms may be modified. Changes will be announced to active clients at least 30 days before taking effect. Continued use of services after modification constitutes acceptance of the new Terms.`,
    },
    {
      heading: "16. Severability and entire agreement",
      body: `If any clause of these Terms is declared invalid, the remainder stays in force.

These Terms, together with the individual service contract and the Privacy Policy, represent the entire agreement between the parties.

Last updated: ${OPERATOR.updated}`,
    },
  ],
  de: [
    {
      heading: "1. Einführung und Annahme",
      body: `Diese Allgemeinen Geschäftsbedingungen (die „AGB") regeln die Nutzung von vorba.dev und die professionellen Dienstleistungen von ${OPERATOR.nameDe} (nachfolgend „vorba", „wir" oder „der Anbieter").

Durch die Nutzung unserer Website oder die Beauftragung unserer Dienstleistungen stimmen Sie diesen AGB zu. Wenn Sie nicht einverstanden sind, nutzen Sie bitte die Dienstleistungen nicht.`,
    },
    {
      heading: "2. Anbieteridentifikation",
      body: `${OPERATOR.nameDe}<br>
Steuernummer (CIF): ${OPERATOR.cui}<br>
Handelsregister: ${OPERATOR.rc}<br>
Geschäftsadresse: ${OPERATOR.address}<br>
Marke: vorba (vorba.dev)<br>
E-Mail: ${OPERATOR.hello}`,
    },
    {
      heading: "3. Leistungsbeschreibung",
      body: `vorba bietet professionelle Dienstleistungen in folgenden Bereichen:
<ul>
<li>Custom Software Development (Web-Apps, interne Systeme, KI-Agenten)</li>
<li>Technisches Audit und digitale Transformation</li>
<li>Strategische Beratung zur Softwarearchitektur</li>
<li>Systemimplementierung und -integration</li>
</ul>
Die Dienstleistungen richten sich AUSSCHLIESSLICH an Geschäftskunden (B2B). Wir bieten keine Verbraucherdienstleistungen (B2C) an.`,
    },
    {
      heading: "4. Vertragsabschluss",
      body: `<ol>
<li>Erstanfrage — über das Formular auf der Website oder direkt an ${OPERATOR.hello}</li>
<li>Kostenloses 48-Stunden-Audit — vorläufige Analyse und erster Plan, ohne Verpflichtung</li>
<li>Kommerzielles Angebot — detailliert, mit Umfang, Lieferungen, Festpreis</li>
<li>Separater Dienstleistungsvertrag — wird unterzeichnet, bevor jegliche Arbeit beginnt</li>
<li>Ausführung und Lieferung — gemäß vereinbarten Meilensteinen</li>
<li>Abnahme und Schlusszahlung</li>
</ol>
Das kostenlose 48-Stunden-Audit ist ein kommerzielles Angebot ohne vertragliche Verpflichtung für beide Seiten.`,
    },
    {
      heading: "5. Vergütung und Zahlung",
      body: `<ul>
<li>Die angezeigten Preise gelten ausschließlich für B2B-Kunden</li>
<li>Standardmodell: Festpreis pro Projekt, keine Stundenabrechnung</li>
<li>Zahlung gemäß Meilensteinplan des Einzelvertrags</li>
<li>Methoden: Banküberweisung, Karte via Stripe</li>
<li>Alle Preise in EUR oder RON, ohne MwSt. (PFA unterhalb der MwSt.-Schwelle; MwSt. wird hinzugefügt, falls steuerpflichtig)</li>
<li>Rechnungen werden gemäß rumänischem Steuerrecht ausgestellt und per SPV/E-Mail übermittelt</li>
<li>Zahlungsfrist: maximal 14 Tage ab Rechnungsdatum, sofern nicht anders vereinbart</li>
</ul>`,
    },
    {
      heading: "6. Lieferung und Abnahme",
      body: `<ul>
<li>Der Liefertermin wird im Einzelvertrag festgelegt</li>
<li>Eine Lieferung gilt als abgenommen, wenn innerhalb von 7 Werktagen nach Lieferung kein schriftliches Feedback eingeht</li>
<li>Umfangsänderungen nach Freigabe werden separat berechnet (Change Request)</li>
</ul>`,
    },
    {
      heading: "7. Geistiges Eigentum",
      body: `<ul>
<li>Für den Kunden entwickelter Custom-Quellcode geht mit Schlusszahlung in das ausschließliche Eigentum des Kunden über</li>
<li>Generische Komponenten, Open-Source-Bibliotheken und Frameworks verbleiben bei ihrer ursprünglichen Quelle</li>
<li>Techniken, Methoden und Know-how von vorba verbleiben bei vorba</li>
<li>vorba darf das Projekt öffentlich (als Case Study) nur mit ausdrücklicher Zustimmung des Kunden referenzieren</li>
</ul>`,
    },
    {
      heading: "8. Vertraulichkeit",
      body: `vorba und der Kunde wahren die Vertraulichkeit der im Rahmen der Zusammenarbeit erhaltenen Geschäftsinformationen für eine Dauer von 5 Jahren nach Vertragsende.

Ausnahmen:
<ul>
<li>Bereits öffentliche Informationen</li>
<li>Von zuständigen Behörden angeforderte Informationen</li>
<li>Allgemeine technische Informationen (Best Practices, architektonische Ansätze)</li>
</ul>`,
    },
    {
      heading: "9. Verarbeitung personenbezogener Daten",
      body: `Während der Vertragsausführung kann vorba auf personenbezogene Daten des Kunden zugreifen. In diesen Fällen handelt vorba als AUFTRAGSVERARBEITER und der Kunde als VERANTWORTLICHER. Es wird ein separater Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO unterzeichnet.

Für Details zur Verarbeitung der Daten von Website-Besuchern siehe unsere <a href="/privacy">Datenschutzerklärung</a>.`,
    },
    {
      heading: "10. Gewährleistung und Haftungsbeschränkung",
      body: `<ul>
<li>vorba garantiert, dass die Dienstleistungen mit der in der Softwarebranche zu erwartenden Sorgfalt erbracht werden</li>
<li>Für innerhalb von 30 Tagen nach Lieferung gemeldete Fehler verpflichtet sich vorba zur kostenlosen Behebung</li>
<li>Nach 30 Tagen ist technischer Support nur im Rahmen eines separaten Wartungsvertrags verfügbar</li>
<li>Die Gesamthaftung von vorba ist auf den im Rahmen des Einzelvertrags gezahlten Betrag begrenzt</li>
</ul>
Wir haften NICHT für:
<ul>
<li>Entgangene Gewinne oder Chancen</li>
<li>Indirekte oder Folgeschäden</li>
<li>Opportunitätskosten</li>
<li>Verluste durch Drittanbieter (Cloud, SaaS)</li>
</ul>`,
    },
    {
      heading: "11. Höhere Gewalt",
      body: `Keine Partei haftet für Verzögerungen oder Nichterfüllung aufgrund höherer Gewalt (Krieg, Naturkatastrophen, offiziell erklärte Pandemien, größere Ausfälle der Internetinfrastruktur).`,
    },
    {
      heading: "12. Kündigung",
      body: `Beide Parteien können den Vertrag kündigen:
<ul>
<li>Einvernehmlich</li>
<li>Bei wesentlicher Verletzung, nach schriftlicher Mahnung und 14-tägiger Heilungsfrist</li>
<li>Bei Insolvenz- oder Konkursverfahren der anderen Partei</li>
</ul>
Bei Vertragsende:
<ul>
<li>Der Kunde zahlt für die bis zum Kündigungsdatum erbrachte Arbeit</li>
<li>vorba gibt alle Kundendaten zurück und löscht interne Kopien innerhalb von 30 Tagen</li>
</ul>`,
    },
    {
      heading: "13. Anwendbares Recht und Gerichtsstand",
      body: `Es gilt rumänisches Recht. Streitigkeiten werden wie folgt beigelegt:
<ul>
<li>Gütlich durch direkte Verhandlung</li>
<li>Durch SAL.ro-Mediation (optional)</li>
<li>Letzte Instanz: zuständige Gerichte am Sitz von vorba (Bukarest)</li>
</ul>
Für Schweizer Kunden: Wir wenden rumänisches Recht an, akzeptieren aber alternativ den Gerichtsstand Zürich für Verträge unter CHF 50.000.`,
    },
    {
      heading: "14. Mitteilungen",
      body: `Alle offiziellen Mitteilungen erfolgen per E-Mail an:
<ul>
<li>vorba: ${OPERATOR.hello} (mit Lesebestätigung)</li>
<li>Kunde: an die bei Vertragsabschluss angegebene Kontaktadresse</li>
</ul>`,
    },
    {
      heading: "15. Änderungen",
      body: `Diese AGB können geändert werden. Änderungen werden aktiven Kunden mindestens 30 Tage vor Inkrafttreten angekündigt. Die weitere Nutzung der Dienstleistungen nach der Änderung gilt als Annahme der neuen AGB.`,
    },
    {
      heading: "16. Salvatorische Klausel und gesamte Vereinbarung",
      body: `Sollte eine Klausel dieser AGB unwirksam sein, bleibt der Rest in Kraft.

Diese AGB stellen zusammen mit dem Einzelvertrag und der Datenschutzerklärung die gesamte Vereinbarung zwischen den Parteien dar.

Letzte Aktualisierung: ${OPERATOR.updated}`,
    },
  ],
};

/* ============================================================
 * IMPRINT / LEGAL NOTICE
 * ============================================================ */
export const IMPRINT: LegalContent = {
  ro: [
    {
      heading: "Date despre furnizor",
      body: `Conform Legii nr. 365/2002 privind comerțul electronic, vă furnizăm următoarele informații:

Denumire: ${OPERATOR.nameRo}<br>
Sediu profesional: ${OPERATOR.address}<br>
CUI: ${OPERATOR.cui}<br>
Nr. Registrul Comerțului: ${OPERATOR.rc}<br>
Email: ${OPERATOR.hello}

Operator brand comercial: vorba<br>
Domeniu web: vorba.dev

<strong>Autoritatea de supraveghere (protecția datelor):</strong><br>
Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)<br>
<a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a>`,
    },
  ],
  en: [
    {
      heading: "Legal notice",
      body: `Per Romanian Law 365/2002 on electronic commerce, we provide the following information:

Name: ${OPERATOR.nameEn}<br>
Business address: ${OPERATOR.address}<br>
Tax ID (CIF): ${OPERATOR.cui}<br>
Romanian Trade Registry: ${OPERATOR.rc}<br>
Email: ${OPERATOR.hello}

Commercial brand: vorba<br>
Domain: vorba.dev

<strong>Data protection supervisory authority:</strong><br>
National Supervisory Authority for Personal Data Processing (ANSPDCP, Romania)<br>
<a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a>`,
    },
  ],
  de: [
    {
      heading: "Impressum",
      body: `Gemäß rumänischem Gesetz Nr. 365/2002 über den elektronischen Geschäftsverkehr:

Name: ${OPERATOR.nameDe}<br>
Geschäftsadresse: ${OPERATOR.address}<br>
Steuernummer (CIF): ${OPERATOR.cui}<br>
Handelsregisternummer (RO): ${OPERATOR.rc}<br>
E-Mail: ${OPERATOR.hello}

Marke: vorba<br>
Domain: vorba.dev

<strong>Aufsichtsbehörde Datenschutz:</strong><br>
Rumänische Datenschutzbehörde (ANSPDCP)<br>
<a href="https://www.dataprotection.ro" target="_blank" rel="noopener">www.dataprotection.ro</a>`,
    },
  ],
};

/* ============================================================
 * UI strings per page per locale
 * ============================================================ */
export const LEGAL_UI: Record<
  Locale,
  {
    back: string;
    disclaimer: string;
    privacyTitle: string;
    termsTitle: string;
    imprintTitle: string;
  }
> = {
  ro: {
    back: "← Înapoi la vorba.dev",
    disclaimer:
      "Template legal — Acest text urmează tipare GDPR standard. Recomandăm review profesional cu avocat înaintea utilizării în contracte de mare valoare.",
    privacyTitle: "Politica de Confidențialitate",
    termsTitle: "Termeni și Condiții",
    imprintTitle: "Date furnizor",
  },
  en: {
    back: "← Back to vorba.dev",
    disclaimer:
      "Legal template — This text follows standard GDPR patterns. Professional legal review is recommended before use in high-value contracts.",
    privacyTitle: "Privacy Policy",
    termsTitle: "Terms & Conditions",
    imprintTitle: "Legal Notice",
  },
  de: {
    back: "← Zurück zu vorba.dev",
    disclaimer:
      "Rechts-Template — Dieser Text folgt Standard-DSGVO-Mustern. Eine professionelle juristische Prüfung wird vor der Verwendung in Verträgen mit hohem Wert empfohlen.",
    privacyTitle: "Datenschutzerklärung",
    termsTitle: "Allgemeine Geschäftsbedingungen",
    imprintTitle: "Impressum",
  },
};
